import { useCallback, useMemo, useState } from "react";

const normalizeFileExtension = (value) =>
  (value || "").toString().trim().replace(/^\./, "").toLowerCase();

const getCapturedDateTimestamp = (record) => {
  const rawCapturedDate =
    record?.dateOnImage ||
    "";

  if (!rawCapturedDate) {
    return null;
  }

  const parsed = new Date(rawCapturedDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const getEngineerName = (record) =>
  (record?.owner || "")
    .toString()
    .trim();

const getEngineerKey = (record) => getEngineerName(record).toLowerCase();

const normalizeEngineerName = (value) =>
  (value || "").toString().trim().toLowerCase();

const getServiceAppointmentNumber = (record) =>
  (
    record?.serviceAppointmentNumber ||
    record?.serviceAppointmentNo ||
    record?.serviceAppointmnetNo ||
    record?.ServiceAppointmentNo ||
    ""
  )
    .toString()
    .trim();

const normalizeSaNumber = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^sa\W*/g, "");

const getFileTypeGroup = (record) => {
  const extension = normalizeFileExtension(
    record?.imageFileExtension || record?.fileExtension || record?.fileName,
  );

  if (["jpg", "jpeg", "png", "heif", "heic"].includes(extension)) {
    return "Images";
  }

  if (["mp4", "avi", "hevc", "mov"].includes(extension)) {
    return "Videos";
  }

  if (
    ["pdf", "xls", "xlsx", "doc", "docx", "ppt", "pptx"].includes(extension)
  ) {
    return "Documents";
  }

  return "Others";
};

const flattenGroupedUsers = (groupedUsers) =>
  groupedUsers.flatMap((woliGroup) =>
    (woliGroup.data || []).flatMap((saGroup) => saGroup.data || []),
  );

const groupRecordsByWorkOrder = (records, groupByServiceAppointment) => {
  const grouped = new Map();

  records.forEach((record) => {
    const woliKey =
      record?.workOrderLineItemNumber || record?.workOrderLineItemID || "";
    let woliGroup = grouped.get(woliKey);

    if (!woliGroup) {
      woliGroup = {
        workOrderLineItemNumber: woliKey,
        data: [],
        _saIndexMap: new Map(),
      };
      grouped.set(woliKey, woliGroup);
    }

    if (!groupByServiceAppointment) {
      if (woliGroup.data.length === 0) {
        woliGroup.data.push({
          serviceAppointmentNumber: "",
          data: [],
        });
      }

      woliGroup.data[0].data.push(record);
      return;
    }

    const saKey =
      record?.serviceAppointmentNumber || record?.serviceAppointmnetID || "";
    let saGroup = woliGroup._saIndexMap.get(saKey);
    if (!saGroup) {
      saGroup = {
        serviceAppointmentNumber: saKey,
        data: [],
      };
      woliGroup._saIndexMap.set(saKey, saGroup);
      woliGroup.data.push(saGroup);
    }

    saGroup.data.push(record);
  });

  return Array.from(grouped.values()).map((woliGroup) => ({
    workOrderLineItemNumber: woliGroup.workOrderLineItemNumber,
    data: woliGroup.data.map((saGroup) => ({
      serviceAppointmentNumber: saGroup.serviceAppointmentNumber,
      data: saGroup.data.map((record, index) => ({
        ...record,
        FileNo: index + 1,
      })),
    })),
  }));
};

const flattenUsersReinstatement = (obj) => {
  let result = [];

  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      result = result.concat(obj[key]);
    } else if (obj[key] && typeof obj[key] === "object") {
      result = result.concat(flattenUsersReinstatement(obj[key]));
    }
  }

  return result;
};

const compareCapturedDates = (left, right, direction = "asc") => {
  const leftTimestamp = getCapturedDateTimestamp(left);
  const rightTimestamp = getCapturedDateTimestamp(right);
  const leftMissing = leftTimestamp === null;
  const rightMissing = rightTimestamp === null;

  if (leftMissing && rightMissing) {
    return 0;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  if (leftTimestamp !== rightTimestamp) {
    return direction === "asc"
      ? leftTimestamp - rightTimestamp
      : rightTimestamp - leftTimestamp;
  }

  return 0;
};

const sortGalleryRecords = (records, sortOption) =>
  [...records].sort((left, right) => {
    if (sortOption === "engineer") {
      const engineerCompare = getEngineerName(left).localeCompare(
        getEngineerName(right),
        undefined,
        { sensitivity: "base" },
      );

      if (engineerCompare !== 0) {
        return engineerCompare;
      }
    } else {
      const dateCompare = compareCapturedDates(
        left,
        right,
        sortOption === "capturedDateDesc" ? "desc" : "asc",
      );
      if (dateCompare !== 0) {
        return dateCompare;
      }
    }

    const saCompare = getServiceAppointmentNumber(left).localeCompare(
      getServiceAppointmentNumber(right),
      undefined,
      { sensitivity: "base" },
    );
    if (saCompare !== 0) {
      return saCompare;
    }

    const ownerCompare = getEngineerName(left).localeCompare(
      getEngineerName(right),
      undefined,
      { sensitivity: "base" },
    );
    if (ownerCompare !== 0) {
      return ownerCompare;
    }

    const fileCompare = (left.fileName || "").localeCompare(
      right.fileName || "",
      undefined,
      { sensitivity: "base" },
    );
    if (fileCompare !== 0) {
      return fileCompare;
    }

    return (left.id || "").localeCompare(right.id || "", undefined, {
      sensitivity: "base",
    });
  });

const groupReinstatementRecords = (records, recordType) => {
  const groupedData = {};
  let fileNoCounter = 1;

  records.forEach((item) => {
    const {
      siteName,
      patchName,
      workOrderLineItemWorkType,
      workOrderLineItemNumber,
      serviceAppointmentNumber,
    } = item;

    const normalizedItem = {
      ...item,
      FileNo: fileNoCounter++,
    };

    if (recordType === "Site" || recordType === "Patch") {
      if (!groupedData[siteName]) {
        groupedData[siteName] = {};
      }

      if (!groupedData[siteName][patchName]) {
        groupedData[siteName][patchName] = {};
      }

      if (!groupedData[siteName][patchName][workOrderLineItemWorkType]) {
        groupedData[siteName][patchName][workOrderLineItemWorkType] = {};
      }

      if (
        !groupedData[siteName][patchName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ]
      ) {
        groupedData[siteName][patchName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ] = {};
      }

      if (
        !groupedData[siteName][patchName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ][serviceAppointmentNumber]
      ) {
        groupedData[siteName][patchName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ][serviceAppointmentNumber] = [];
      }

      groupedData[siteName][patchName][workOrderLineItemWorkType][
        workOrderLineItemNumber
      ][serviceAppointmentNumber].push(normalizedItem);
    } else if (recordType === "Reinstatement_Site_WOLI") {
      if (!groupedData[siteName]) {
        groupedData[siteName] = {};
      }

      if (!groupedData[siteName][workOrderLineItemWorkType]) {
        groupedData[siteName][workOrderLineItemWorkType] = {};
      }

      if (
        !groupedData[siteName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ]
      ) {
        groupedData[siteName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ] = {};
      }

      if (
        !groupedData[siteName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ][serviceAppointmentNumber]
      ) {
        groupedData[siteName][workOrderLineItemWorkType][
          workOrderLineItemNumber
        ][serviceAppointmentNumber] = [];
      }

      groupedData[siteName][workOrderLineItemWorkType][
        workOrderLineItemNumber
      ][serviceAppointmentNumber].push(normalizedItem);
    }
  });

  return groupedData;
};

export const useGalleryControls = ({
  users,
  usersReinstatement,
  directUsers = [],
  recordType,
  setCurrentPage,
  resetGallerySelection,
}) => {
  const [isGalleryControlsOpen, setIsGalleryControlsOpen] = useState(false);
  const [galleryControlsMode, setGalleryControlsMode] = useState("sort");
  const [sortOption, setSortOption] = useState("capturedDateAsc");
  const [groupByServiceAppointment, setGroupByServiceAppointment] = useState(false);
  const [selectedFileTypeGroups, setSelectedFileTypeGroups] = useState([]);
  const [capturedDateFrom, setCapturedDateFrom] = useState("");
  const [capturedDateTo, setCapturedDateTo] = useState("");
  const [isSingleDateMode, setIsSingleDateMode] = useState(false);
  const [includeNoCapturedDate, setIncludeNoCapturedDate] = useState(true);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedServiceAppointments, setSelectedServiceAppointments] = useState([]);
  const [engineerSearch, setEngineerSearch] = useState("");
  const [saSearch, setSaSearch] = useState("");

  const updateGalleryFilters = (callback) => {
    callback();
    setCurrentPage(1);
    resetGallerySelection();
  };

  const recordMatchesActiveFilters = useCallback((record) => {
    if (
      selectedFileTypeGroups.length > 0 &&
      !selectedFileTypeGroups.includes(getFileTypeGroup(record))
    ) {
      return false;
    }

    const recordCapturedDate = getCapturedDateTimestamp(record);
    if (recordCapturedDate === null) {
      return includeNoCapturedDate;
    }

    // const fromTimestamp = capturedDateFrom
    //   ? new Date(`${capturedDateFrom}T00:00:00`).getTime()
    //   : null;
    // const toTimestamp = capturedDateTo
    //   ? new Date(`${capturedDateTo}T23:59:59.999`).getTime()
    //   : null;
    const fromTimestamp = capturedDateFrom
      ? new Date(`${capturedDateFrom}T00:00:00`).getTime()
      : null;

    const toTimestamp = isSingleDateMode
      ? capturedDateFrom
        ? new Date(`${capturedDateFrom}T23:59:59.999`).getTime()
        : null
      : capturedDateTo
        ? new Date(`${capturedDateTo}T23:59:59.999`).getTime()
        : null;

    if (fromTimestamp !== null && recordCapturedDate < fromTimestamp) {
      return false;
    }

    if (toTimestamp !== null && recordCapturedDate > toTimestamp) {
      return false;
    }

    if (
      selectedEngineers.length > 0 &&
      !selectedEngineers
        .map(normalizeEngineerName)
        .includes(getEngineerKey(record))
    ) {
      return false;
    }

    if (selectedServiceAppointments.length > 0) {
      const recordSa = normalizeSaNumber(getServiceAppointmentNumber(record));
      const normalizedSelectedSa = selectedServiceAppointments.map((s) =>
        normalizeSaNumber(s),
      );

      if (!normalizedSelectedSa.includes(recordSa)) {
        return false;
      }
    }

    return true;
  }, [
    capturedDateFrom,
    capturedDateTo,
    includeNoCapturedDate,
    selectedEngineers,
    selectedFileTypeGroups,
    selectedServiceAppointments,
  ]);

  const toggleFileTypeGroup = (groupName) => {
    updateGalleryFilters(() => {
      setSelectedFileTypeGroups((prevGroups) =>
        prevGroups.includes(groupName)
          ? prevGroups.filter((item) => item !== groupName)
          : [...prevGroups, groupName],
      );
    });
  };

  const handleSortChange = (value) => {
    updateGalleryFilters(() => {
      setSortOption(value);
    });
  };

  const toggleGroupByServiceAppointment = () => {
    updateGalleryFilters(() => {
      setGroupByServiceAppointment((prev) => !prev);
    });
  };

  const openGalleryControls = (mode) => {
    setGalleryControlsMode(mode);
    setIsGalleryControlsOpen((prevOpen) =>
      prevOpen && galleryControlsMode === mode ? false : true,
    );
  };

  const closeGalleryControls = () => {
    setIsGalleryControlsOpen(false);
  };

  const handleCapturedDateFromChange = (value) => {
    updateGalleryFilters(() => {
      setCapturedDateFrom(value);
    });
  };

  const handleCapturedDateToChange = (value) => {
    updateGalleryFilters(() => {
      setCapturedDateTo(value);
    });
  };

  const handleSingleDateModeChange = useCallback(
    (checked) => {
      setIsSingleDateMode(checked);

      if (checked) {
        setCapturedDateTo("");
      }

      resetGallerySelection();
    },
    [resetGallerySelection],
  );


  const handleIncludeNoCapturedDateChange = (checked) => {
    updateGalleryFilters(() => {
      setIncludeNoCapturedDate(checked);
    });
  };

  const toggleEngineerSelection = (engineerName) => {
    updateGalleryFilters(() => {
      setSelectedEngineers((prevSelected) =>
        prevSelected.includes(engineerName)
          ? prevSelected.filter((item) => item !== engineerName)
          : [...prevSelected, engineerName],
      );
    });
  };

  const toggleServiceAppointmentSelection = (saNumber) => {
    updateGalleryFilters(() => {
      setSelectedServiceAppointments((prevSelected) =>
        prevSelected.includes(saNumber)
          ? prevSelected.filter((item) => item !== saNumber)
          : [...prevSelected, saNumber],
      );
    });
  };

  const clearGalleryFilters = () => {
    setSortOption("capturedDateDesc");
    setSelectedFileTypeGroups([]);
    setCapturedDateFrom("");
    setCapturedDateTo("");
    setIsSingleDateMode(false);
    setIncludeNoCapturedDate(true);
    setSelectedEngineers([]);
    setSelectedServiceAppointments([]);
    setEngineerSearch("");
    setSaSearch("");
    setCurrentPage(1);
    resetGallerySelection();
  };

  const displayUsers = useMemo(() => {
    if (!users.length) {
      return [];
    }

    const flatUsers = flattenGroupedUsers(users);
    const filtered = flatUsers.filter(recordMatchesActiveFilters);
    const sortedRecords = sortGalleryRecords(filtered, sortOption);

    return groupRecordsByWorkOrder(sortedRecords, groupByServiceAppointment);
  }, [
    recordMatchesActiveFilters,
    sortOption,
    users,
    groupByServiceAppointment,
  ]);

  const filteredUsersReinstatement = useMemo(() => {
    if (!usersReinstatement || Object.keys(usersReinstatement).length === 0) {
      return {};
    }

    const flatUsers = flattenUsersReinstatement(usersReinstatement);
    if (!flatUsers.length) {
      return {};
    }

    const filtered = flatUsers.filter(recordMatchesActiveFilters);

    const sortedRecords = sortGalleryRecords(filtered, sortOption);
    return groupReinstatementRecords(sortedRecords, recordType);
  }, [
    recordType,
    recordMatchesActiveFilters,
    sortOption,
    usersReinstatement,
  ]);

  const filteredDirectUsers = useMemo(() => {
    if (!directUsers.length) {
      return [];
    }

    const filtered = directUsers.filter(recordMatchesActiveFilters);
    return sortGalleryRecords(filtered, sortOption);
  }, [
    directUsers,
    recordMatchesActiveFilters,
    sortOption,
  ]);

  const allStandardRecords = useMemo(
    () => (users.length ? flattenGroupedUsers(users) : []),
    [users],
  );

  const allReinstatementRecords = useMemo(
    () =>
      usersReinstatement && Object.keys(usersReinstatement).length
        ? flattenUsersReinstatement(usersReinstatement)
        : [],
    [usersReinstatement],
  );

  const engineerOptions = useMemo(
    () =>
      Array.from(
        [...allStandardRecords, ...allReinstatementRecords]
          .reduce((acc, record) => {
            const ownerName = getEngineerName(record);
            const ownerKey = normalizeEngineerName(ownerName);

            if (ownerKey && !acc.some((item) => item.key === ownerKey)) {
              acc.push({ key: ownerKey, label: ownerName });
            }

            return acc;
          }, []),
      )
        .sort((left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base",
          }),
        )
        .map((item) => item.label),
    [allStandardRecords, allReinstatementRecords],
  );

  const saOptions = useMemo(
    () =>
      Array.from(
        [...allStandardRecords, ...allReinstatementRecords]
          .reduce((acc, record) => {
            const saNumber = getServiceAppointmentNumber(record);
            if (saNumber && !acc.includes(saNumber)) {
              acc.push(saNumber);
            }
            return acc;
          }, []),
      ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" })),
    [allStandardRecords, allReinstatementRecords],
  );

  const arrayWoli = useMemo(() => {
    if (users.length > 0) {
      return displayUsers.flatMap((item) =>
        item.data.flatMap((innerItem) => innerItem.data),
      );
    }

    return flattenUsersReinstatement(filteredUsersReinstatement);
  }, [displayUsers, filteredUsersReinstatement, users.length]);

  const ImgUri = useMemo(
    () =>
      arrayWoli.map((user) =>
        [
          user.finalImageURL,
          user.fileName,
          user.imageFileExtension,
          user.imageFileSize,
        ].join("$"),
      ),
    [arrayWoli],
  );

  return {
    isGalleryControlsOpen,
    setIsGalleryControlsOpen,
    galleryControlsMode,
    setGalleryControlsMode,
    sortOption,
    groupByServiceAppointment,
    selectedFileTypeGroups,
    capturedDateFrom,
    capturedDateTo,  
    isSingleDateMode,
    handleSingleDateModeChange,
    includeNoCapturedDate,
    selectedEngineers,
    selectedServiceAppointments,
    engineerSearch,
    saSearch,
    setEngineerSearch,
    setSaSearch,
    displayUsers,
    filteredUsersReinstatement,
    filteredDirectUsers,
    engineerOptions,
    saOptions,
    arrayWoli,
    ImgUri,
    toggleFileTypeGroup,
    handleSortChange,
    toggleGroupByServiceAppointment,
    openGalleryControls,
    closeGalleryControls,
    handleCapturedDateFromChange,
    handleCapturedDateToChange,
    handleIncludeNoCapturedDateChange,
    toggleEngineerSelection,
    toggleServiceAppointmentSelection,
    clearGalleryFilters,
  };
};
